/**
 * @file pages/Register.js
 * @description Page d'inscription
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Register = () => {
  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  if (isAuthenticated) {
    navigate('/moments', { replace: true });
    return null;
  }

  const validate = () => {
    const newErrors = {};
    if (!form.name || form.name.length < 2) newErrors.name = 'Le nom doit contenir au moins 2 caractères';
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Email invalide';
    if (!form.password || form.password.length < 8) newErrors.password = 'Minimum 8 caractères';
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(form.password)) {
      newErrors.password = 'Doit contenir une majuscule, une minuscule et un chiffre';
    }
    if (form.password !== form.confirmPassword) newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    return newErrors;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
    setApiError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      navigate('/moments');
    } catch (err) {
      const msg = err.response?.data?.error || 
        err.response?.data?.errors?.[0]?.msg || 
        'Erreur lors de la création du compte.';
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = () => {
    const p = form.password;
    if (!p) return null;
    let score = 0;
    if (p.length >= 8) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    const levels = ['Très faible', 'Faible', 'Moyen', 'Fort', 'Très fort'];
    const colors = ['#e74c3c', '#e67e22', '#f1c40f', '#27ae60', '#1abc9c'];
    return { label: levels[score], color: colors[score], score };
  };

  const strength = getPasswordStrength();

  return (
    <>
      <style>{`
        .auth-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 120px 24px 60px;
          background: linear-gradient(135deg, #1F2A44 0%, #141d30 100%);
          position: relative;
        }
        .auth-page::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse 60% 50% at 50% 0%, rgba(169, 134, 103, 0.12), transparent 60%);
        }
        .auth-card {
          position: relative;
          width: 100%;
          max-width: 500px;
          background: rgba(31, 42, 68, 0.7);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(169, 134, 103, 0.2);
          border-radius: 24px;
          padding: 48px 40px;
          animation: fadeInUp 0.5s ease;
        }
        .auth-logo-icon {
          width: 60px;
          height: 60px;
          background: rgba(169, 134, 103, 0.15);
          border: 1px solid rgba(169, 134, 103, 0.3);
          border-radius: 16px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.6rem;
          margin-bottom: 20px;
        }
        .auth-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 2.2rem;
          color: #FFFFFF;
          text-align: center;
          margin-bottom: 8px;
        }
        .auth-subtitle {
          color: rgba(240, 234, 226, 0.55);
          text-align: center;
          font-size: 0.95rem;
          margin-bottom: 36px;
        }
        .form-group { margin-bottom: 20px; }
        .form-label {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: rgba(240, 234, 226, 0.7);
          margin-bottom: 8px;
        }
        .form-input {
          width: 100%;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(169, 134, 103, 0.2);
          border-radius: 10px;
          padding: 14px 16px;
          color: #FFFFFF;
          font-size: 0.95rem;
          transition: all 0.2s ease;
          outline: none;
        }
        .form-input:focus {
          border-color: #A98667;
          background: rgba(169, 134, 103, 0.08);
          box-shadow: 0 0 0 3px rgba(169, 134, 103, 0.12);
        }
        .form-input.error { border-color: #e74c3c; }
        .form-input::placeholder { color: rgba(240, 234, 226, 0.3); }
        .field-error {
          color: #e74c3c;
          font-size: 0.8rem;
          margin-top: 6px;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .password-strength {
          margin-top: 8px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .strength-bars {
          display: flex;
          gap: 4px;
          flex: 1;
        }
        .strength-bar {
          height: 3px;
          flex: 1;
          border-radius: 2px;
          background: rgba(255,255,255,0.1);
          transition: background 0.3s;
        }
        .strength-label {
          font-size: 0.75rem;
          color: rgba(240, 234, 226, 0.6);
        }
        .auth-error {
          background: rgba(231, 76, 60, 0.12);
          border: 1px solid rgba(231, 76, 60, 0.3);
          border-radius: 10px;
          padding: 12px 16px;
          color: #e74c3c;
          font-size: 0.9rem;
          margin-bottom: 20px;
        }
        .auth-submit {
          width: 100%;
          background: linear-gradient(135deg, #A98667, #c4a07d);
          color: #FFFFFF;
          border: none;
          border-radius: 10px;
          padding: 16px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 8px;
        }
        .auth-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(169, 134, 103, 0.4);
        }
        .auth-submit:disabled { opacity: 0.7; cursor: not-allowed; }
        .auth-footer {
          text-align: center;
          margin-top: 28px;
          font-size: 0.9rem;
          color: rgba(240, 234, 226, 0.55);
        }
        .auth-footer a { color: #A98667; font-weight: 600; }
        .auth-footer a:hover { color: #c4a07d; }
        @media (max-width: 500px) { .auth-card { padding: 36px 24px; } }
      `}</style>

      <div className="auth-page">
        <div className="auth-card">
          <div style={{ textAlign: 'center' }}>
            <div className="auth-logo-icon">✦</div>
          </div>
          <h1 className="auth-title">Créer un compte</h1>
          <p className="auth-subtitle">Commencez votre parcours spirituel dès aujourd'hui</p>

          {apiError && <div className="auth-error">⚠ {apiError}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="name">Nom complet</label>
              <input
                id="name"
                type="text"
                name="name"
                className={`form-input ${errors.name ? 'error' : ''}`}
                placeholder="Jean Dupont"
                value={form.name}
                onChange={handleChange}
                required
              />
              {errors.name && <div className="field-error">⚠ {errors.name}</div>}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="email">Adresse email</label>
              <input
                id="email"
                type="email"
                name="email"
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="votre@email.com"
                value={form.email}
                onChange={handleChange}
                required
              />
              {errors.email && <div className="field-error">⚠ {errors.email}</div>}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="password">Mot de passe</label>
              <input
                id="password"
                type="password"
                name="password"
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="Minimum 8 caractères"
                value={form.password}
                onChange={handleChange}
                required
              />
              {form.password && strength && (
                <div className="password-strength">
                  <div className="strength-bars">
                    {[0,1,2,3].map(i => (
                      <div
                        key={i}
                        className="strength-bar"
                        style={{ background: i < strength.score ? strength.color : 'rgba(255,255,255,0.1)' }}
                      />
                    ))}
                  </div>
                  <span className="strength-label" style={{ color: strength.color }}>{strength.label}</span>
                </div>
              )}
              {errors.password && <div className="field-error">⚠ {errors.password}</div>}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="confirmPassword">Confirmer le mot de passe</label>
              <input
                id="confirmPassword"
                type="password"
                name="confirmPassword"
                className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                placeholder="••••••••"
                value={form.confirmPassword}
                onChange={handleChange}
                required
              />
              {errors.confirmPassword && <div className="field-error">⚠ {errors.confirmPassword}</div>}
            </div>

            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? 'Création du compte...' : 'Créer mon compte'}
            </button>
          </form>

          <div className="auth-footer">
            Déjà un compte ?{' '}
            <Link to="/login">Se connecter</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
